/**
 * Renderers for the icon, avatar and gradient element types.
 *
 * These fill the gaps that stop an admin drawing a platform-exact preview with
 * the existing primitives: a Facebook "like", an Instagram bookmark, a Story's
 * sound toggle, a profile picture with a gradient ring, the scrim behind a Reel
 * caption.
 *
 * There is deliberately no glyph catalogue and no platform knowledge in this
 * file. Everything is driven by the sub-component's `settings_json`, seeded by
 * `ChromeComponentSeeder` and mirrored onto the saved element, so adding a
 * glyph, a ring style or a new platform's furniture is a seeder row — no
 * frontend change and no redeploy.
 *
 * Each type renders a fully detached element: placed, dragged, resized,
 * restyled and deleted on its own, exactly like a text or shape element.
 *
 * Shared by the builder canvas and the campaign-detail preview so an element
 * looks identical while designing and while reviewing.
 */

import React from "react";

// ─── Icon ─────────────────────────────────────────────────────────────────

/**
 * Glyph geometry from the backend. Glyphs are raw SVG paths rather than an
 * imported icon set — that is what keeps the catalogue backend-driven. `paths`
 * is a list so multi-stroke glyphs (a verified badge, a muted speaker with its
 * slash) need no special handling.
 */
export interface IconGeometry {
  view_box?: string;
  paths?: string[];
  filled?: boolean;
  stroke_width?: number;
  stroke_linecap?: "butt" | "round" | "square";
  stroke_linejoin?: "miter" | "round" | "bevel";
}

/** Per-element overrides, stored on the element as `iconSettings`. */
export interface IconSettings {
  color?: string;
  filled?: boolean;
  strokeWidth?: number;
  opacity?: number;
  rotation?: number;
  /** Optional counter placed around the icon, e.g. a like count. */
  count?: string;
  /**
   * Where the count sits relative to the icon. `below` is kept (rather than
   * renamed to `bottom`) so canvases saved before the other positions existed
   * keep rendering unchanged.
   */
  countPosition?: "left" | "right" | "top" | "below" | "none";
  countColor?: string;
  countFontSize?: number;
  countFontWeight?: number | string;
}

interface ChromeIconProps {
  geometry?: IconGeometry | null;
  settings?: IconSettings | null;
  /** Shown when the backend supplied no geometry. */
  label?: string;
}

/**
 * One glyph, scaled to fill its element box.
 *
 * `preserveAspectRatio` keeps the glyph undistorted when the element is resized,
 * so resizing behaves like every other element.
 */
export const ChromeIcon: React.FC<ChromeIconProps> = ({
  geometry,
  settings,
  label,
}) => {
  const paths = geometry?.paths ?? [];
  const color = settings?.color ?? "#000000";
  const filled = settings?.filled ?? geometry?.filled ?? false;
  const countPosition = settings?.countPosition ?? "none";
  const hasCount =
    countPosition !== "none" && settings?.count != null && settings.count !== "";

  if (paths.length === 0) {
    // No geometry yet — a visible, selectable placeholder so the element can
    // still be positioned rather than becoming an invisible empty box.
    return (
      <div className="w-full h-full border border-dashed border-gray-300 flex items-center justify-center">
        <span className="text-[9px] text-gray-400 px-1 truncate">
          {label ?? "Icon"}
        </span>
      </div>
    );
  }

  const glyph = (
    <svg
      viewBox={geometry?.view_box ?? "0 0 24 24"}
      preserveAspectRatio="xMidYMid meet"
      style={{
        width: "100%",
        height: "100%",
        transform: settings?.rotation
          ? `rotate(${settings.rotation}deg)`
          : undefined,
        overflow: "visible",
      }}
      aria-hidden="true"
    >
      {paths.map((d, i) => (
        <path
          key={i}
          d={d}
          fill={filled ? color : "none"}
          stroke={filled ? "none" : color}
          strokeWidth={settings?.strokeWidth ?? geometry?.stroke_width ?? 2}
          strokeLinecap={geometry?.stroke_linecap ?? "round"}
          strokeLinejoin={geometry?.stroke_linejoin ?? "round"}
        />
      ))}
    </svg>
  );

  if (!hasCount) {
    return (
      <div
        className="w-full h-full flex items-center justify-center"
        style={{ opacity: settings?.opacity ?? 1 }}
      >
        {glyph}
      </div>
    );
  }

  // The count stacks vertically for top/below and sits inline for left/right.
  // `reversed` puts the count first, which is what makes left and top work
  // without duplicating the markup.
  const isVertical = countPosition === "top" || countPosition === "below";
  const reversed = countPosition === "left" || countPosition === "top";

  const countNode = (
    <span
      style={{
        color: settings?.countColor ?? color,
        fontSize: settings?.countFontSize ?? 12,
        fontWeight: settings?.countFontWeight ?? 600,
        whiteSpace: "nowrap",
        lineHeight: 1,
        // Never squeezed out by the glyph, and never forces the row wider
        // than the element: it clips itself rather than the glyph.
        flexShrink: 0,
        overflow: "hidden",
      }}
    >
      {settings.count}
    </span>
  );

  const glyphNode = (
    /*
      The glyph keeps a square footprint so the count never distorts it, but
      it must also be allowed to shrink: an element sized for the glyph alone
      would otherwise push the count outside the box, and the canvas clips
      overflow, which cut glyphs in half and truncated counts. `minWidth: 0`
      plus a flexible basis lets the row fit whatever width it is given.
    */
    <div
      style={{
        height: isVertical ? "62%" : "100%",
        aspectRatio: "1 / 1",
        flexShrink: 1,
        minWidth: 0,
      }}
      className="flex items-center justify-center"
    >
      {glyph}
    </div>
  );

  return (
    <div
      className={`w-full h-full flex items-center ${
        isVertical ? "flex-col justify-center" : "flex-row justify-start"
      }`}
      style={{ opacity: settings?.opacity ?? 1, gap: isVertical ? 2 : 5 }}
    >
      {reversed ? countNode : glyphNode}
      {reversed ? glyphNode : countNode}
    </div>
  );
};

// ─── Avatar ───────────────────────────────────────────────────────────────

/** Per-element overrides, stored on the element as `avatarSettings`. */
export interface AvatarSettings {
  url?: string | null;
  /** Monogram source when there is no image. */
  initials?: string;
  shape?: "circle" | "rounded" | "square";
  ring?: "none" | "solid" | "gradient";
  ringColor?: string;
  ringGradient?: string;
  ringWidth?: number;
  /** Gap between ring and image, as platforms leave one. */
  ringGap?: number;
  ringGapColor?: string;
  background?: string;
  textColor?: string;
  opacity?: number;
}

interface ChromeAvatarProps {
  settings?: AvatarSettings | null;
  /** Uploaded media, so a new avatar shows immediately. */
  url?: string | null;
  label?: string;
}

/**
 * Circular/rounded profile image with an optional ring. Reachable only awkwardly
 * with a shape plus an image today, and not at all with a gradient ring.
 */
export const ChromeAvatar: React.FC<ChromeAvatarProps> = ({
  settings,
  url,
  label,
}) => {
  const shape = settings?.shape ?? "circle";
  const radius = shape === "circle" ? "50%" : shape === "rounded" ? "18%" : "0";
  const ring = settings?.ring ?? "none";
  const ringWidth = ring === "none" ? 0 : (settings?.ringWidth ?? 2);
  const ringGap = ring === "none" ? 0 : (settings?.ringGap ?? 2);
  const imageUrl = url ?? settings?.url ?? null;

  const ringBackground =
    ring === "gradient"
      ? (settings?.ringGradient ??
        "linear-gradient(45deg,#F58529,#DD2A7B,#8134AF,#515BD4)")
      : ring === "solid"
        ? (settings?.ringColor ?? "#FF0069")
        : "transparent";

  const inner = imageUrl ? (
    <img
      src={imageUrl}
      alt={label ?? "avatar"}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        borderRadius: radius,
        display: "block",
      }}
    />
  ) : (
    <div
      style={{
        width: "100%",
        height: "100%",
        borderRadius: radius,
        background: settings?.background ?? "#D1D5DB",
        color: settings?.textColor ?? "#FFFFFF",
        // Scales with the element so the monogram stays proportionate.
        fontSize: "42%",
        fontWeight: 600,
      }}
      className="flex items-center justify-center select-none"
    >
      {settings?.initials ?? ""}
    </div>
  );

  return (
    <div
      className="w-full h-full"
      style={{
        opacity: settings?.opacity ?? 1,
        borderRadius: radius,
        background: ringBackground,
        padding: ringWidth,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          borderRadius: radius,
          background: settings?.ringGapColor ?? "#FFFFFF",
          padding: ringGap,
          boxSizing: "border-box",
        }}
      >
        {inner}
      </div>
    </div>
  );
};

// ─── Gradient ─────────────────────────────────────────────────────────────

/** Per-element overrides, stored on the element as `gradientSettings`. */
export interface GradientSettings {
  /** Full CSS gradient. Wins over the from/to shorthand when set. */
  css?: string;
  from?: string;
  to?: string;
  angle?: number;
  type?: "linear" | "radial";
  opacity?: number;
  borderRadius?: number | string;
}

interface ChromeGradientProps {
  settings?: GradientSettings | null;
}

/**
 * A gradient scrim — the fade platforms put behind overlaid text so a caption
 * stays legible over bright creative. The shape element is solid-fill only.
 */
export const ChromeGradient: React.FC<ChromeGradientProps> = ({ settings }) => {
  const from = settings?.from ?? "rgba(0,0,0,0.7)";
  const to = settings?.to ?? "rgba(0,0,0,0)";
  const background =
    settings?.css ??
    (settings?.type === "radial"
      ? `radial-gradient(circle, ${from} 0%, ${to} 100%)`
      : `linear-gradient(${settings?.angle ?? 180}deg, ${from} 0%, ${to} 100%)`);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        background,
        opacity: settings?.opacity ?? 1,
        borderRadius: settings?.borderRadius ?? 0,
      }}
    />
  );
};

// ─── Settings resolution ──────────────────────────────────────────────────
//
// Both renderers read the same backend `settings_json`, so the builder and the
// preview always agree. Snake_case and camelCase are both accepted: the seeder
// emits snake_case, while values round-tripped through `aggregated_json` may
// come back camelCased.

export const iconGeometryFromSettings = (
  settings?: Record<string, any> | null,
): IconGeometry | null => {
  const geometry = settings?.icon_geometry ?? settings?.iconGeometry;
  if (!geometry) return null;
  return {
    view_box: geometry.view_box ?? geometry.viewBox,
    paths: Array.isArray(geometry.paths)
      ? geometry.paths
      : geometry.path
        ? [geometry.path]
        : [],
    filled: geometry.filled,
    stroke_width: geometry.stroke_width ?? geometry.strokeWidth,
    stroke_linecap: geometry.stroke_linecap ?? geometry.strokeLinecap,
    stroke_linejoin: geometry.stroke_linejoin ?? geometry.strokeLinejoin,
  };
};

export const iconSettingsFromDefaults = (
  settings?: Record<string, any> | null,
): IconSettings => {
  const defaults = settings?.icon_defaults ?? settings?.iconDefaults ?? {};
  return {
    color: defaults.color ?? "#000000",
    filled: defaults.filled ?? false,
    strokeWidth: defaults.stroke_width ?? defaults.strokeWidth ?? 2,
    opacity: defaults.opacity ?? 1,
    countPosition: defaults.count_position ?? defaults.countPosition ?? "none",
    count: defaults.count ?? "",
    countFontSize: defaults.count_font_size ?? defaults.countFontSize ?? 12,
  };
};

export const avatarSettingsFromDefaults = (
  settings?: Record<string, any> | null,
): AvatarSettings => {
  const defaults = settings?.avatar_defaults ?? settings?.avatarDefaults ?? {};
  return {
    shape: defaults.shape ?? "circle",
    ring: defaults.ring ?? "none",
    ringColor: defaults.ring_color ?? defaults.ringColor,
    ringGradient: defaults.ring_gradient ?? defaults.ringGradient,
    ringWidth: defaults.ring_width ?? defaults.ringWidth ?? 2,
    ringGap: defaults.ring_gap ?? defaults.ringGap ?? 2,
    ringGapColor: defaults.ring_gap_color ?? defaults.ringGapColor ?? "#FFFFFF",
    background: defaults.background ?? "#D1D5DB",
    initials: defaults.initials ?? "",
    opacity: defaults.opacity ?? 1,
  };
};

export const gradientSettingsFromDefaults = (
  settings?: Record<string, any> | null,
): GradientSettings => {
  const defaults =
    settings?.gradient_defaults ?? settings?.gradientDefaults ?? {};
  return {
    css: defaults.css,
    from: defaults.from ?? "rgba(0,0,0,0.7)",
    to: defaults.to ?? "rgba(0,0,0,0)",
    angle: defaults.angle ?? 180,
    type: defaults.type ?? "linear",
    opacity: defaults.opacity ?? 1,
  };
};

/** Drop size seeded per sub-component, so glyphs land square not letterboxed. */
export const defaultRectFromSettings = (
  settings?: Record<string, any> | null,
): { width: number; height: number } | null => {
  const rect = settings?.default_rect ?? settings?.defaultRect;
  if (!rect?.width || !rect?.height) return null;
  return { width: rect.width, height: rect.height };
};
